// sessionsApi.js
import { request } from "./http";
import { readStoredAuth } from "../auth/authStorage";

// ISO strings для OffsetDateTime на бэке
function toIso(dt) {
    if (!dt) return null;
    if (typeof dt === "string") return dt;
    return new Date(dt).toISOString();
}

// -------- normalizers --------

function pickArray(r) {
    if (Array.isArray(r)) return r;
    return r?.content || r?.items || r?.data || [];
}

function toIntOrNull(x) {
    if (x == null) return null;
    const n = Number(x);
    if (!Number.isFinite(n)) return null;
    return Math.round(n);
}

function cleanText(value, max = 300) {
    const text = String(value ?? "").trim();
    if (!text) return "";
    return text.slice(0, max);
}

function asObject(value) {
    return value && typeof value === "object" ? value : {};
}

function unwrapItem(raw) {
    const source = asObject(raw);
    if (source.item && typeof source.item === "object") return source.item;
    return source;
}

function toBookingRef(value) {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    return text;
}

function extractRoomToken(raw) {
    const source = asObject(raw);
    const nestedRoom = asObject(source.sessionRoom || source.session_room || source.room);
    const token =
        source.roomToken ??
        source.room_token ??
        source.token ??
        source.inviteToken ??
        source.invite_token ??
        source.sessionRoomToken ??
        source.session_room_token ??
        nestedRoom.roomToken ??
        nestedRoom.room_token ??
        nestedRoom.token ??
        nestedRoom.sessionRoomToken ??
        nestedRoom.session_room_token ??
        null;
    const normalized = String(token || "").trim();
    return normalized || "";
}

function extractCandidateBookingIds(sourceRaw) {
    const source = asObject(sourceRaw);
    const nestedRoom = asObject(source.sessionRoom || source.session_room || source.room);
    const nestedBooking = asObject(source.booking || source.sessionBooking || source.session_booking);
    const refs = [
        source.bookingId,
        source.booking_id,
        source.sessionBookingId,
        source.session_booking_id,
        source.roomBookingId,
        source.room_booking_id,
        source.sessionRoomBookingId,
        source.session_room_booking_id,
        source.booking?.id,
        source.sessionRoom?.bookingId,
        source.session_room?.booking_id,
        nestedBooking.id,
        nestedBooking.bookingId,
        nestedBooking.booking_id,
        nestedRoom.bookingId,
        nestedRoom.booking_id,
        nestedRoom.sessionBookingId,
        nestedRoom.session_booking_id,
        source.id,
    ]
        .map(toBookingRef)
        .filter(Boolean);
    return [...new Set(refs)];
}

function normalizeParticipant(raw, fallbackRole = "guest") {
    const source = asObject(raw);
    const displayName = cleanText(
        source.display_name ?? source.displayName ?? source.fullName ?? source.name,
        140
    );
    const email = cleanText(source.email, 190).toLowerCase();
    const role = cleanText(source.role || fallbackRole || "guest", 32).toLowerCase();
    const externalUserId = cleanText(
        source.external_user_id ?? source.externalUserId ?? source.user_id ?? source.userId ?? source.id,
        80
    );

    if (!displayName && !email && !externalUserId) return null;

    const participant = {
        role: role || "guest",
        display_name: displayName || "",
    };
    if (displayName) participant.name = displayName;
    if (email) participant.email = email;
    if (externalUserId) participant.external_user_id = externalUserId;
    return participant;
}

function buildGroupSessionCreatePayload(dto) {
    const payload = { ...asObject(dto) };
    const me = asObject(readStoredAuth()?.me);

    const hostDisplayName = cleanText(
        payload.host_display_name ??
            payload.hostDisplayName ??
            me.displayName ??
            me.name ??
            me.fullName ??
            me.login,
        140
    );
    const hostEmail = cleanText(payload.host_email ?? payload.hostEmail ?? me.email, 190).toLowerCase();
    const hostExternalId = cleanText(
        payload.host_external_user_id ??
            payload.hostExternalUserId ??
            me.externalUserId ??
            me.userId ??
            me.id,
        80
    );

    if (hostDisplayName) {
        payload.host_display_name = hostDisplayName;
        payload.hostDisplayName = hostDisplayName;
    }
    if (hostEmail) {
        payload.host_email = hostEmail;
        payload.hostEmail = hostEmail;
    }
    if (hostExternalId) {
        payload.host_external_user_id = hostExternalId;
        payload.hostExternalUserId = hostExternalId;
    }

    const participantsInput = Array.isArray(payload.participants) ? payload.participants : [];
    let participants = participantsInput
        .map((item) => normalizeParticipant(item))
        .filter(Boolean);

    const hasPsychologist = participants.some((item) => String(item?.role || "").toLowerCase() === "psychologist");

    if (!hasPsychologist && (hostDisplayName || hostEmail || hostExternalId)) {
        const hostParticipant = normalizeParticipant(
            {
                role: "psychologist",
                display_name: hostDisplayName,
                email: hostEmail,
                external_user_id: hostExternalId,
            },
            "psychologist"
        );
        if (hostParticipant) participants = [hostParticipant, ...participants];
    }

    if (participants.length) {
        payload.participants = participants;
    }

    return payload;
}

async function bootstrapAegisRoomAfterGroupSessionCreate(createdRaw, dtoRaw) {
    const created = unwrapItem(createdRaw);
    const dto = asObject(dtoRaw);

    const directRoomToken = extractRoomToken(created);
    if (directRoomToken) {
        await request(`/api/session/room/${encodeURIComponent(directRoomToken)}/aegis/sync`, {
            method: "POST",
        });
        return;
    }

    const bookingIds = [
        ...extractCandidateBookingIds(created),
        ...extractCandidateBookingIds(dto),
    ];
    for (const bookingId of bookingIds) {
        let room = null;
        try {
            room = unwrapItem(
                await request(`/api/session/rooms/by-booking/${encodeURIComponent(String(bookingId))}`)
            );
        } catch {
            try {
                room = unwrapItem(
                    await request(`/api/session/rooms/${encodeURIComponent(String(bookingId))}`, {
                        method: "POST",
                    })
                );
            } catch {
                room = null;
            }
        }

        const roomToken = extractRoomToken(room);
        if (!roomToken) continue;

        try {
            await request(`/api/session/room/${encodeURIComponent(roomToken)}/aegis/sync`, {
                method: "POST",
            });
        } catch {
            // no-op: fallback should never break session creation flow
        }
        return;
    }
}

// -------- schedule helpers --------

const DEFAULT_SCHEDULE = {
    slotMinutes: 50,
    bufferMinutes: 0,
    week: {
        1: [{ start: "10:00", end: "18:00" }],
        2: [{ start: "10:00", end: "18:00" }],
        3: [{ start: "10:00", end: "18:00" }],
        4: [{ start: "10:00", end: "18:00" }],
        5: [{ start: "10:00", end: "16:00" }],
        6: [],
        7: [],
    },
};

const DAY_TO_KEY = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7,
};

const KEY_TO_DAY = {
    1: "MONDAY",
    2: "TUESDAY",
    3: "WEDNESDAY",
    4: "THURSDAY",
    5: "FRIDAY",
    6: "SATURDAY",
    7: "SUNDAY",
};

function hhmm(t) {
    if (!t) return "00:00";
    const s = String(t);
    return s.length >= 5 ? s.slice(0, 5) : s;
}

function scheduleFromWorkingRows(rows) {
    const week = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
    const slotMinutes =
        rows?.find((x) => x?.slotDurationMinutes)?.slotDurationMinutes ??
        DEFAULT_SCHEDULE.slotMinutes;

    for (const r of rows || []) {
        const key = DAY_TO_KEY[String(r?.dayOfWeek || "").toUpperCase()] || null;
        if (!key) continue;
        week[key] = [
            ...(week[key] || []),
            { start: hhmm(r?.startTime), end: hhmm(r?.endTime) },
        ];
    }

    for (const k of Object.keys(week)) {
        week[k] = (week[k] || [])
            .slice()
            .sort((a, b) => String(a.start).localeCompare(String(b.start)));
    }

    return { slotMinutes, bufferMinutes: 0, week };
}

function workingRowsFromSchedule(schedule) {
    const slotMinutes = schedule?.slotMinutes ?? DEFAULT_SCHEDULE.slotMinutes;
    const week = schedule?.week || {};
    const out = [];

    for (const [k, intervals] of Object.entries(week)) {
        const day = KEY_TO_DAY[Number(k)];
        if (!day) continue;
        for (const it of Array.isArray(intervals) ? intervals : []) {
            out.push({
                dayOfWeek: day,
                startTime: hhmm(it.start),
                endTime: hhmm(it.end),
                slotDurationMinutes: slotMinutes,
            });
        }
    }
    return out;
}

export const sessionsApi = {
    // --- Schedule ---
    async getSchedule(psychologistId) {
        try {
            const rows = await request(
                `/api/psychologists/${psychologistId}/working-schedule`
            );
            if (Array.isArray(rows)) return scheduleFromWorkingRows(rows);
            if (rows && rows.week) return rows;
            return DEFAULT_SCHEDULE;
        } catch (primaryError) {
            try {
                const legacy = await request(
                    `/api/psychologists/${psychologistId}/schedule`
                );
                if (legacy && legacy.week) return legacy;
            } catch {
                throw primaryError;
            }
            throw primaryError;
        }
    },

    async upsertSchedule(psychologistId, schedule) {
        try {
            const payload = workingRowsFromSchedule(schedule);
            const r = await request(
                `/api/psychologists/${psychologistId}/working-schedule`,
                {
                    method: "PUT",
                    json: payload,
                }
            );
            if (Array.isArray(r)) return scheduleFromWorkingRows(r);
            return r?.week ? r : schedule;
        } catch (primaryError) {
            try {
                const r = await request(`/api/psychologists/${psychologistId}/schedule`, {
                    method: "PUT",
                    json: schedule,
                });
                return r?.week ? r : schedule;
            } catch {
                throw primaryError;
            }
        }
    },

    // --- WorkBreaks ---
    async listWorkBreaks(psychologistId, { from, to } = {}) {
        const qs = new URLSearchParams();
        if (from) qs.set("from", from);
        if (to) qs.set("to", to);
        const tail = qs.toString() ? `?${qs.toString()}` : "";
        const r = await request(
            `/api/psychologists/${psychologistId}/work-breaks${tail}`
        );
        return (pickArray(r) || []).map((x) => {
            const resolvedId =
                x?.id ??
                x?.breakId ??
                x?.workBreakId ??
                x?.work_break_id ??
                x?.work_breakId ??
                null;
            return resolvedId != null && x?.id == null ? { ...x, id: resolvedId } : x;
        });
    },

    async upsertWorkBreak(psychologistId, b) {
        const payload = {
            id: b?.id ?? null,
            date: b?.date ?? null,
            dayOfWeek: b?.dayOfWeek ?? null,
            startTime: hhmm(b?.startTime),
            endTime: hhmm(b?.endTime),
            type: b?.type ?? b?.kind ?? null,
            kind: b?.kind ?? b?.type ?? null,
            note: cleanText(b?.note ?? b?.reason ?? b?.comment, 300) || null,
            reason: cleanText(b?.reason ?? b?.note ?? b?.comment, 300) || null,
        };

        if (payload.id) {
            return await request(`/api/work-breaks/${payload.id}`, {
                method: "PUT",
                json: payload,
            });
        }
        return await request(`/api/psychologists/${psychologistId}/work-breaks`, {
            method: "POST",
            json: payload,
        });
    },

    async deleteWorkBreak(psychologistId, breakId) {
        void psychologistId;
        return await request(`/api/work-breaks/${breakId}`, { method: "DELETE" });
    },

    // --- DayOffs ---
    async listDayOffs(psychologistId, { from, to } = {}) {
        const qs = new URLSearchParams();
        if (from) qs.set("from", from);
        if (to) qs.set("to", to);
        const tail = qs.toString() ? `?${qs.toString()}` : "";
        const r = await request(`/api/psychologists/${psychologistId}/day-offs${tail}`);
        // Some backends return non-standard id field names (dayOffId, dayOffID, etc.).
        // Normalize to `id` so UI can edit/delete existing day-offs reliably.
        const arr = pickArray(r);
        return (arr || []).map((x) => {
            const resolvedId =
                x?.id ??
                x?.dayOffId ??
                x?.dayOffID ??
                x?.day_off_id ??
                x?.day_offId ??
                x?.day_offID ??
                x?.dayoffId ??
                x?.dayoffID ??
                x?.dayoff_id ??
                x?.dayOff?.id ??
                null;
            return resolvedId != null && x?.id == null ? { ...x, id: resolvedId } : x;
        });
    },

    async createDayOff(psychologistId, { date, reason } = {}) {
        return await request(`/api/psychologists/${psychologistId}/day-offs`, {
            method: "POST",
            json: { date, reason },
        });
    },

    // Backends may return day-offs without an id (id: null). In that case we delete by date.
    // Supports:
    //  - deleteDayOff(psyId, id)
    //  - deleteDayOff(psyId, { id, date })
    async deleteDayOff(psychologistId, dayOff) {
        const dayOffId = typeof dayOff === "object" ? dayOff?.id : dayOff;
        const dayOffDate = typeof dayOff === "object" ? dayOff?.date : null;

        if (dayOffId != null) {
            return await request(`/api/day-offs/${dayOffId}`, { method: "DELETE" });
        }

        // If there is no id, try delete-by-date. We try query first (common REST style),
        // then JSON body as a fallback.
        if (dayOffDate) {
            const qs = new URLSearchParams();
            qs.set("date", dayOffDate);
            try {
                return await request(
                    `/api/psychologists/${psychologistId}/day-offs?${qs.toString()}`,
                    { method: "DELETE" }
                );
            } catch {
                // Some servers don't route DELETE with query; try body.
                return await request(`/api/psychologists/${psychologistId}/day-offs`, {
                    method: "DELETE",
                    json: { date: dayOffDate },
                });
            }
        }

        throw new Error("Не удалось удалить выходной: отсутствует id и date");
    },

    // --- Bookings (private for PSYCHO/ADMIN) ---
    async listBookings(psychologistId, { from, to } = {}) {
        const qs = new URLSearchParams();
        if (from) qs.set("from", from);
        if (to) qs.set("to", to);
        const tail = qs.toString() ? `?${qs.toString()}` : "";
        const r = await request(`/api/psychologists/${psychologistId}/bookings${tail}`);
        return pickArray(r);
    },

    // --- Calendar (public for CLIENT) ---
    async listCalendar(psychologistId, { from, to } = {}) {
        const qs = new URLSearchParams();
        if (from) qs.set("from", from);
        if (to) qs.set("to", to);
        const tail = qs.toString() ? `?${qs.toString()}` : "";
        const r = await request(`/api/psychologists/${psychologistId}/calendar${tail}`);
        return pickArray(r);
    },

    async createBooking(psychologistId, { startDateTime, endDateTime, price } = {}) {
        const payload = {
            startDateTime: toIso(startDateTime),
            endDateTime: toIso(endDateTime),
            price: toIntOrNull(price),
        };

        return await request(`/api/psychologists/${psychologistId}/bookings`, {
            method: "POST",
            json: payload,
        });
    },

    // --- Group sessions (psychologist self) ---
    async listMyGroupSessions({ page = 0, size = 24, q, status, from, to } = {}) {
        const qs = new URLSearchParams();
        if (page != null) qs.set("page", String(page));
        if (size != null) qs.set("size", String(size));
        if (q) qs.set("q", String(q));
        if (status) qs.set("status", String(status));
        if (from) qs.set("from", String(from));
        if (to) qs.set("to", String(to));
        const tail = qs.toString() ? `?${qs.toString()}` : "";
        return await request(`/api/me/group-sessions${tail}`);
    },

    async getMyGroupSession(id) {
        return await request(`/api/me/group-sessions/${id}`);
    },

    async createMyGroupSession(dto) {
        const payload = buildGroupSessionCreatePayload(dto);
        const created = await request(`/api/me/group-sessions`, { method: "POST", json: payload });
        try {
            await bootstrapAegisRoomAfterGroupSessionCreate(created, payload);
        } catch {
            // no-op: Aegis bootstrap is best-effort and must not break create API success
        }
        return created;
    },

    async updateMyGroupSession(id, dto) {
        return await request(`/api/me/group-sessions/${id}`, { method: "PUT", json: dto });
    },

    async closeMyGroupSession(id) {
        return await request(`/api/me/group-sessions/${id}/close`, { method: "POST" });
    },

    async startPayment(
        bookingId,
        { returnUrl, promoCode = null, certificateCode = null } = {}
    ) {
        const promo = String(promoCode || "").trim() || null;
        const certificate = String(certificateCode || "").trim() || null;
        return await request(`/api/bookings/${bookingId}/pay`, {
            method: "POST",
            json: {
                returnUrl: returnUrl || null,
                promoCode: promo,
                certificateCode: certificate,
            },
        });
    },

    async getPaymentStatus(bookingId) {
        return await request(`/api/bookings/${bookingId}/payment-status`);
    },

    async getBooking(bookingId) {
        return await request(`/api/bookings/${bookingId}`);
    },

    async cancelBooking(bookingId) {
        return await request(`/api/bookings/${bookingId}/cancel`, { method: "POST" });
    },

    async updateBookingStatus(bookingId, status) {
        const id = String(bookingId || "").trim();
        const statusToken = String(status || "").trim().toUpperCase();
        if (!id) throw new Error("bookingId is required");
        if (!statusToken) throw new Error("status is required");
        return await request(`/api/bookings/${id}/status`, {
            method: "PATCH",
            json: { status: statusToken },
        });
    },

    listPublicGroupSessions: ({ page = 0, size = 24, q, themeIds, methodIds, minPrice, maxPrice } = {}) => {
        const qs = new URLSearchParams();
        qs.set("page", String(page));
        qs.set("size", String(size));

        if (q && String(q).trim()) qs.set("q", String(q).trim());

        (themeIds || []).forEach((id) => qs.append("themeId", String(id)));
        (methodIds || []).forEach((id) => qs.append("methodId", String(id)));

        if (Number.isFinite(minPrice)) qs.set("minPrice", String(minPrice));
        if (Number.isFinite(maxPrice)) qs.set("maxPrice", String(maxPrice));

        // ✅ пример эндпоинта — поправь под свой бэк
        return request(`/api/group-sessions?${qs.toString()}`);
    },

    getPublicGroupSession: (id) => request(`/api/group-sessions/${id}`),

    joinGroupSession: (id) => request(`/api/group-sessions/${id}/join`, { method: "POST" }),

    startGroupPayment: (
        id,
        { returnUrl, promoCode = null, certificateCode = null } = {}
    ) => {
        const promo = String(promoCode || "").trim() || null;
        const certificate = String(certificateCode || "").trim() || null;
        return request(`/api/group-sessions/${id}/pay`, {
            method: "POST",
            json: { returnUrl: returnUrl || null, promoCode: promo, certificateCode: certificate },
        });
    },

    // --- Flexible Schedule ---

    /**
     * Конвертер: формат редактора → бэкенд PUT payload
     * editor: { slotMinutes, flexible: [{dateFrom, week: {1:[{start,end}],...}}] }
     * backend: { weeks: [{dateFrom, slotDurationMinutes, week: {MONDAY:[...],...}}] }
     */
    flexEditorToBackend(value) {
        const KEY_TO_DAY = {1:"MONDAY",2:"TUESDAY",3:"WEDNESDAY",4:"THURSDAY",5:"FRIDAY",6:"SATURDAY",7:"SUNDAY"};
        const slotDurationMinutes = value?.slotMinutes ?? 50;
        const weeks = (value?.flexible ?? []).map(entry => {
            const week = {};
            for (let k = 1; k <= 7; k++) {
                week[KEY_TO_DAY[k]] = (entry.week?.[k] ?? []).filter(
                    iv => iv?.start && iv?.end && iv.start < iv.end
                );
            }
            return { dateFrom: entry.dateFrom, slotDurationMinutes, week };
        });
        return { weeks };
    },

    /**
     * Конвертер: бэкенд ответ → формат редактора
     */
    flexBackendToEditor(backendWeeks) {
        const DAY_TO_KEY = {MONDAY:1,TUESDAY:2,WEDNESDAY:3,THURSDAY:4,FRIDAY:5,SATURDAY:6,SUNDAY:7};
        if (!Array.isArray(backendWeeks) || backendWeeks.length === 0) {
            return { slotMinutes: 50, flexible: [] };
        }
        const slotMinutes = backendWeeks[0]?.slotDurationMinutes ?? 50;
        const flexible = backendWeeks.map(entry => {
            const week = {};
            for (let k = 1; k <= 7; k++) week[k] = [];
            if (entry.week) {
                for (const [dayName, intervals] of Object.entries(entry.week)) {
                    const k = DAY_TO_KEY[dayName];
                    if (k) week[k] = Array.isArray(intervals) ? intervals : [];
                }
            }
            return { dateFrom: entry.dateFrom, week };
        });
        return { slotMinutes, flexible };
    },

    async getFlexibleSchedule(psychologistId, { from, to } = {}) {
        try {
            const qs = new URLSearchParams();
            if (from) qs.set("from", from);
            if (to) qs.set("to", to);
            const q = qs.toString();
            const r = await request(
                `/api/psychologists/${psychologistId}/flexible-schedule${q ? "?" + q : ""}`
            );
            return this.flexBackendToEditor(r?.weeks ?? r ?? []);
        } catch (e) {
            return { slotMinutes: 50, flexible: [] };
        }
    },

    async saveFlexibleSchedule(psychologistId, value) {
        const payload = this.flexEditorToBackend(value);
        const r = await request(
            `/api/psychologists/${psychologistId}/flexible-schedule`,
            { method: "PUT", json: payload }
        );
        return this.flexBackendToEditor(r?.weeks ?? r ?? []);
    },

};
